import vr from "../../../utilFunctions/validationRules";

const {
    lenBetween,
    isPositiveInt,
} = vr;

const isEmpty = (v) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

const computeValidation = (val, rule, R = "Required") => {
    const empty = isEmpty(val);

    if (rule.required && empty) {
        return { error: true, skipped: true, message: rule.requiredMessage || R };
    }
    if (rule.pattern && !rule.pattern(val)) {
        return { error: true, skipped: true, message: rule.invalidMessage || R };
    }
    return { error: false, skipped: true, message: "" };
};

const buildPartRules = (t) => {
    const R = t?.common?.required_field || "Required";
    const rangeChars = (min, max) =>
        t?.common?.range_chars?.replace?.("{min}", min)?.replace?.("{max}", max);
    const maxChars = (max) =>
        t?.common?.max_chars?.replace?.("{max}", max);
    const invalidValue = t?.common?.invalid_value;
    const positive = t?.common?.positive_number;

    return {
        name: {
            required: true,
            requiredMessage: R,
            invalidMessage: rangeChars(3, 150),
            pattern: (v) => typeof v === "string" && lenBetween(v.trim(), 3, 150),
            error: false,
            skipped: false,
        },

        serialNumber: {
            required: true,
            requiredMessage: R,
            invalidMessage: maxChars(150),
            pattern: (v) => typeof v === "string" && v.trim() !== "" && v.trim().length <= 150,
            error: false,
            skipped: false,
        },

        costPer: {
            required: true,
            requiredMessage: R,
            invalidMessage: R,
            pattern: (v) => typeof v === "string" && v.trim() !== "",
            error: false,
            skipped: false,
        },

        partType: {
            required: true,
            requiredMessage: R,
            invalidMessage: R,
            pattern: (v) => typeof v === "string" && v.trim() !== "",
            error: false,
            skipped: false,
        },

        category: {
            required: true,
            requiredMessage: R,
            invalidMessage: invalidValue,
            pattern: (v) => isPositiveInt(Number(v)),
            error: false,
            skipped: false,
        },

        isNonStock: {
            required: true,
            requiredMessage: R,
            invalidMessage: invalidValue,
            pattern: (v) => typeof v === "boolean",
            error: false,
            skipped: false,
        },

        description: {
            required: false,
            invalidMessage: rangeChars(3, 200),
            pattern: (v) =>
                v == null ||
                String(v).trim() === "" ||
                lenBetween(String(v).trim(), 3, 200),
            error: false,
            skipped: false,
        },

        people: {
            required: false,
            requiredMessage: t?.common?.select_people_or_teams || "Select at least one person or team",
            invalidMessage: invalidValue,
            pattern: (v) =>
                v == null ||
                (Array.isArray(v) && (v.length === 0 || v.every(id => isPositiveInt(Number(id))))),
            error: false,
            skipped: false,
            message: t?.common?.select_people_or_teams || "Select at least one person or team",
        },

        teams: {
            required: false,
            requiredMessage: t?.common?.select_people_or_teams || "Select at least one person or team",
            invalidMessage: invalidValue,
            pattern: (v) =>
                v == null ||
                v === "" ||
                isPositiveInt(Number(v)),
            error: false,
            skipped: false,
            message: t?.common?.select_people_or_teams || "Select at least one person or team",
        },

        vendor: {
            required: false,
            invalidMessage: invalidValue,
            pattern: (v) =>
                v == null ||
                (Array.isArray(v) && (v.length === 0 || v.every(id => isPositiveInt(Number(id))))),
            error: false,
            skipped: false,
        },

        asset: {
            required: false,
            invalidMessage: invalidValue,
            pattern: (v) =>
                v == null ||
                (Array.isArray(v) && (v.length === 0 || v.every(id => isPositiveInt(Number(id))))),
            error: false,
            skipped: false,
        },

        barcode: {
            required: false,
            pattern: (v) => v == null || typeof v === "boolean",
            error: false,
            skipped: false,
        },

        qrcode: {
            required: false,
            pattern: (v) => v == null || typeof v === "boolean",
            error: false,
            skipped: false,
        },

        quantityPerStore: [buildInventoryRowRules(t)],
    };
};

const buildInventoryRowRules = (t) => {
    const R = t?.common?.required_field || "Required";
    const invalidValue = t?.common?.invalid_value || "Invalid value";
    const positive = t?.common?.positive_number || "Must be 0 or higher";
    const validDigits = t?.common?.valid_digits || "Enter a valid number";
    const minLtMax = t?.parts?.minQty_valid || "Minimum must be less than maximum";
    const maxGtMin = t?.parts?.maxQty_valid || "Maximum must be greater than minimum";

    return {
        location: {
            required: true,
            requiredMessage: R,
            invalidMessage: invalidValue,
            pattern: (v) => isPositiveInt(Number(v)),
            error: true,
            skipped: false,
            message: R,
        },

        quantity: {
            required: true,
            requiredMessage: R,
            invalidMessage: positive,
            pattern: (v) => {
                if (v === null || v === undefined || v === "") return false;
                const num = Number(v);
                return !isNaN(num) && num >= 0 && /^(\d{1,8})(\.\d{1,4})?$/.test(String(v));
            },
            error: true,
            skipped: false,
            message: R,
        },

        unitCost: {
            required: true,
            invalidMessage: positive,
            pattern: (v) => {
                if (v == null || v === "") return false;
                const num = Number(v);
                return !isNaN(num) && num >= 0 && /^(\d{1,8})(\.\d{1,4})?$/.test(String(v));
            },
            error: true,
            skipped: false,
        },

        minimumQuantity: {
            required: true,
            invalidMessage: positive,
            pattern: (v) => {
                if (v == null || v === "") return false;
                const num = Number(v);
                return !isNaN(num) && num >= 0 && /^(\d{1,8})(\.\d{1,4})?$/.test(String(v));
            },
            error: true,
            skipped: false,
            message: minLtMax,
        },

        maximumQuantity: {
            required: true,
            invalidMessage: positive,
            pattern: (v) => {
                if (v == null || v === "") return false;
                const num = Number(v);
                return !isNaN(num) && num >= 0 && /^(\d{1,8})(\.\d{1,4})?$/.test(String(v));
            },
            error: true,
            skipped: false,
            message: maxGtMin,
        },
    };
};

const validateAllParts = (data, validation, t) => {
    const keys = [
        "name",
        "serialNumber",
        "costPer",
        "partType",
        "category",
        "isNonStock",
        "description",
    ];
    const next = { ...validation };

    keys.forEach((k) => {
        const rule = next[k];
        if (!rule) return;
        const val = data[k];
        next[k] = {
            ...rule,
            ...computeValidation(val, rule, t?.common?.required_field)
        };
    });

    next.quantityPerStore = (data.quantityPerStore || []).map((row) => {
        const rowRules = buildInventoryRowRules(t);
        const cooked = {};

        Object.entries(rowRules).forEach(([field, rule]) => {
            cooked[field] = {
                ...rule,
                ...computeValidation(row[field], rule, t?.common?.required_field)
            };
        });

        const min = row?.minimumQuantity;
        const max = row?.maximumQuantity;

        const minNum = min != null && min !== "" ? Number(min) : null;
        const maxNum = max != null && max !== "" ? Number(max) : null;

        if (minNum != null && maxNum != null && minNum >= maxNum) {
            cooked.minimumQuantity = {
                ...cooked.minimumQuantity,
                error: true,
                skipped: true,
                message: rowRules.minimumQuantity.message
            };
            cooked.maximumQuantity = {
                ...cooked.maximumQuantity,
                error: true,
                skipped: true,
                message: rowRules.maximumQuantity.message
            };
        }

        return cooked;
    });

    const hasTopErrors = keys.some((k) => next[k]?.error);
    const hasRowErrors = next.quantityPerStore?.some((r) =>
        Object.values(r).some((c) => c.error)
    );

    return { validation: next, hasErrors: hasTopErrors || hasRowErrors };
};

export {
    buildPartRules,
    buildInventoryRowRules,
    validateAllParts,
    computeValidation,
    isEmpty
};